"use strict";
/**
 * Orderbook Staleness Indicator
 *
 * Drives a minimal freshness cue around the orders-page orderbook widget:
 * a soft GREEN glow while data is confirmed fresh, switching to a RED glow
 * plus an info icon (with an explanatory tooltip) when the book goes stale or
 * errors. Before the first book arrives the cells shimmer ("loading"), then
 * read "No orderbook yet" ("empty") after LOADING_TIMEOUT_MS.
 *
 * Two independent staleness sources are tracked (see CLAUDE.md / the feature
 * brief):
 *   1. SERVER (WS) staleness — the exchange feed hasn't updated our server-side
 *      cache. Surfaced via `serverAgeMs`, computed server-side to dodge clock
 *      skew, then extrapolated forward locally between polls.
 *   2. UI (poll) staleness — the client itself has stopped receiving fresh
 *      polls (a stalled/errored poll loop). Detected from the wall-clock gap
 *      since the last successful update, so it trips even when polling silently
 *      dies and no callback ever fires again.
 *
 * The module keeps a tiny piece of mutable state fed by the polling layer
 * (`notifyOrderbook*`) and a low-frequency ticker that re-evaluates and paints
 * the DOM. The evaluation logic (`evaluateStaleness`) is a pure function so it
 * can be unit-tested without timers or a DOM.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOADING_TIMEOUT_MS = exports.UI_STALE_THRESHOLD_MS = exports.WS_STALE_THRESHOLD_MS = void 0;
exports.formatStaleDuration = formatStaleDuration;
exports.evaluateStaleness = evaluateStaleness;
exports.notifyOrderbookFresh = notifyOrderbookFresh;
exports.notifyOrderbookEmpty = notifyOrderbookEmpty;
exports.notifyOrderbookError = notifyOrderbookError;
exports.startStalenessMonitor = startStalenessMonitor;
exports.stopStalenessMonitor = stopStalenessMonitor;
exports.getStalenessView = getStalenessView;
exports.__resetForTest = __resetForTest;
// --- Thresholds (see AskUserQuestion: "Moderate") ---------------------------
/** Server book age (ms) beyond which the exchange feed is considered stale. */
exports.WS_STALE_THRESHOLD_MS = 1500;
/** Client poll gap (ms) beyond which the UI is considered to have stalled. */
exports.UI_STALE_THRESHOLD_MS = 1000;
/** No first book by this long after start → "empty" instead of "loading". */
exports.LOADING_TIMEOUT_MS = 3000;
/** How often the monitor re-evaluates and repaints. */
const TICK_INTERVAL_MS = 200;
const DEFAULT_THRESHOLDS = {
    wsStaleMs: exports.WS_STALE_THRESHOLD_MS,
    uiStaleMs: exports.UI_STALE_THRESHOLD_MS,
};
// --- Pure evaluation --------------------------------------------------------
/** Format a millisecond duration compactly, e.g. "0.8s", "3s", "12s". */
function formatStaleDuration(ms) {
    const seconds = Math.max(0, ms) / 1000;
    if (seconds < 10) {
        // One decimal below 10s so sub-second staleness reads sensibly.
        return `${seconds.toFixed(1)}s`;
    }
    return `${Math.round(seconds)}s`;
}
/**
 * Decide the indicator state from a snapshot. Pure: no DOM, no clock reads —
 * `now` is passed in so tests are deterministic.
 *
 * Precedence: an explicit error wins; then a stalled poll loop (the more
 * fundamental failure — nothing is arriving at all); then a stale server feed.
 */
function evaluateStaleness(state, now, thresholds = DEFAULT_THRESHOLDS) {
    if (state.errorMessage) {
        return { status: "stale", message: state.errorMessage };
    }
    // Nothing has loaded yet (cold start / 503) → loading, not red.
    if (!state.hasData || state.lastClientUpdateAt === null) {
        if (state.startedAt === null)
            return { status: "neutral", message: null };
        const waited = now - state.startedAt;
        return { status: waited >= exports.LOADING_TIMEOUT_MS ? "empty" : "loading", message: null };
    }
    const uiGapMs = now - state.lastClientUpdateAt;
    if (uiGapMs > thresholds.uiStaleMs) {
        return {
            status: "stale",
            message: `Orderbook not updating — last refresh ${formatStaleDuration(uiGapMs)} ago`,
        };
    }
    // Extrapolate the server-reported age forward to "now".
    if (state.lastServerAgeMs !== null && state.lastServerSampleAt !== null) {
        const serverAgeMs = state.lastServerAgeMs + (now - state.lastServerSampleAt);
        if (serverAgeMs > thresholds.wsStaleMs) {
            return {
                status: "stale",
                message: `No exchange updates for ${formatStaleDuration(serverAgeMs)}`,
            };
        }
    }
    return { status: "fresh", message: null };
}
// --- Live state + DOM monitor ----------------------------------------------
function freshState() {
    return {
        lastClientUpdateAt: null,
        lastServerAgeMs: null,
        lastServerSampleAt: null,
        errorMessage: null,
        hasData: false,
        startedAt: null,
    };
}
let state = freshState();
let tickerId = null;
let thresholds = DEFAULT_THRESHOLDS;
/** A successful poll delivered book data. */
function notifyOrderbookFresh(serverAgeMs) {
    const now = Date.now();
    state.lastClientUpdateAt = now;
    state.lastServerAgeMs = serverAgeMs;
    state.lastServerSampleAt = serverAgeMs !== null ? now : null;
    state.errorMessage = null;
    state.hasData = true;
    paint();
}
/**
 * A poll completed but there was no book yet (cold start / 503 / empty book).
 * Clears any transient error without marking data as received, so the widget
 * stays neutral rather than flashing red before the first book arrives.
 */
function notifyOrderbookEmpty() {
    state.errorMessage = null;
    paint();
}
/** A poll failed. */
function notifyOrderbookError(message) {
    state.errorMessage = message || "Orderbook error";
    paint();
}
/**
 * Start the staleness monitor for a fresh market selection. Resets state and
 * begins ticking. Idempotent — safe to call again without a prior stop.
 */
function startStalenessMonitor(overrides) {
    thresholds = { ...DEFAULT_THRESHOLDS, ...overrides };
    state = { ...freshState(), startedAt: Date.now() };
    if (tickerId === null) {
        tickerId = window.setInterval(paint, TICK_INTERVAL_MS);
    }
    paint();
}
/** Stop the monitor, reset state, and clear the indicator back to neutral. */
function stopStalenessMonitor() {
    if (tickerId !== null) {
        clearInterval(tickerId);
        tickerId = null;
    }
    state = freshState();
    thresholds = DEFAULT_THRESHOLDS;
    applyView({ status: "neutral", message: null });
}
/** Test/inspection helper: current computed view. */
function getStalenessView(now = Date.now()) {
    return evaluateStaleness(state, now, thresholds);
}
function paint() {
    applyView(evaluateStaleness(state, Date.now(), thresholds));
}
// --- DOM ---------------------------------------------------------------------
let lastPaintedStatus = null;
let lastPaintedMessage = null;
/**
 * Reflect the computed view into the DOM: toggle glow classes on the orderbook
 * container and set the tooltip text on the info icon. No-ops when the elements
 * aren't present (e.g. other pages), and skips redundant writes.
 */
function applyView(view) {
    if (view.status === lastPaintedStatus &&
        view.message === lastPaintedMessage) {
        return;
    }
    lastPaintedStatus = view.status;
    lastPaintedMessage = view.message;
    const container = document.getElementById("market-orderbook");
    if (container) {
        container.classList.toggle("is-fresh", view.status === "fresh");
        container.classList.toggle("is-stale", view.status === "stale");
        container.classList.toggle("is-loading", view.status === "loading");
        container.classList.toggle("is-empty", view.status === "empty");
    }
    const indicator = document.getElementById("orderbook-staleness");
    const tooltip = document.getElementById("orderbook-staleness-tooltip");
    const isStale = view.status === "stale";
    if (indicator) {
        indicator.classList.toggle("visible", isStale);
        if (isStale && view.message) {
            indicator.setAttribute("title", view.message);
        }
        else {
            indicator.removeAttribute("title");
        }
    }
    if (tooltip) {
        tooltip.textContent = isStale && view.message ? view.message : "";
    }
}
/** Test-only: reset module singleton state between cases. */
function __resetForTest() {
    if (tickerId !== null) {
        clearInterval(tickerId);
        tickerId = null;
    }
    state = freshState();
    thresholds = DEFAULT_THRESHOLDS;
    lastPaintedStatus = null;
    lastPaintedMessage = null;
}
